<script setup lang="ts">
import { computed, inject } from 'vue';

const props = defineProps<{
  modelValue?: string | number;
  value: string | number;
  label?: string;
  disabled?: boolean;
  name?: string;
  radioColor?: string;
  radioBorderColor?: string;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: string | number];
}>();

// Пытаемся получить контекст группы
const radioGroup = inject('radioGroup', null);

// Используем контекст группы или локальные пропсы
const groupSelectedValue = radioGroup?.selectedValue;
const groupSelectOption = radioGroup?.selectOption;
const groupDisabled = radioGroup?.disabled;
const groupName = radioGroup?.name;

const isChecked = computed(() => {
  if (radioGroup) {
    return groupSelectedValue?.value === props.value;
  }
  return props.modelValue === props.value;
});

const isDisabled = computed(() => {
  if (radioGroup) {
    return groupDisabled?.value || props.disabled;
  }
  return props.disabled;
});

const radioName = computed(() => {
  if (radioGroup) {
    return groupName?.value || props.name;
  }
  return props.name;
});

const radioButtonStyle = computed(() => {
  const style: Record<string, string> = {};
  // Встановлюємо border-color за замовчуванням або з пропа
  style.borderColor = props.radioBorderColor || '#CFCFCF';
  // Застосовуємо колір фону кнопки завжди, якщо задано
  if (props.radioColor) {
    style.backgroundColor = props.radioColor;
  }
  return style;
});

const radioDotStyle = computed(() => ({}));

function handleChange() {
  if (isDisabled.value) return;
  
  if (radioGroup) {
    // Используем контекст группы
    groupSelectOption?.(props.value);
  } else {
    // Используем локальный emit
    emit('update:modelValue', props.value);
  }
}
</script>
<template>
  <div 
    class="radio-item"
    :class="{ disabled: isDisabled, checked: isChecked }"
    @click="handleChange"
  >
    <input
      type="radio"
      :name="radioName"
      :value="value"
      :checked="isChecked"
      :disabled="isDisabled"
      class="radio-input"
      @change="handleChange"
    />
    <div 
      class="radio-button"
    >
      <div 
        class="radio-dot" 
        :class="{ visible: isChecked }"
        :style="radioDotStyle"
      ></div>
    </div>
    <span class="radio-label" v-if="label">{{ label }}</span>
  </div>
</template>

<style scoped>
.radio-item {
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  /* padding: 8px; */
  border-radius: 6px;
  transition: background-color 0.2s ease;
  position: relative;
}



.radio-item.disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.radio-input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
  width: 0;
  height: 0;
}

.radio-button {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  width: 40px;
  height: 40px;
  padding: 5px;
  border-radius: 50%;
  /* background-color: var(--checkbox-bg); */
  border: 1pt solid #CFCFCF;
  transition: border-color 0.2s ease, background-color 0.2s ease;
  flex-shrink: 0;
  background-color: var(--checkbox-bg);
}

.radio-item.disabled .radio-button {
  border-color: var(--text-secondary);
  background-color: var(--forms-bg-disabled);
}

.radio-dot {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 20px;
  height: 20px;
  min-width: 20px;
  min-height: 20px;
  border-radius: 50%;
  background-color: var(--radio-dot-color, #52CFBB);
  opacity: 0;
  transition: opacity 0.2s ease;
  flex-shrink: 0;
}

.radio-dot.visible {
  opacity: 1;
}

.radio-label {
  color: var(--text-primary);
  font-size: 14px;
  line-height: 1.5;
  user-select: none;
  flex: 1;
}

.radio-item.disabled .radio-label {
  color: var(--text-secondary);
}
</style>
